/**
 * Overrides for AWS Amplify Services
 *
 * Sometimes the default behavior of the Amplify SDK does not fit the needs
 * of the application, and these overrides provide a way to customize that behavior.
 *
 * This file contains:
 * 1.) Overrides for the default behavior of AWS Amplify Services.
 * 2.) Additional utility functions to support custom behavior.
 */

import * as Auth from 'aws-amplify/auth';

import AmplifyActions from './Amplify.actions';
import { getStoreRef } from '/b2b/storeRef';
import merge from 'lodash/merge';
import { syncSessionStorage } from '@aws-amplify/core';

// Copy of the internal Cognito SIGN_IN_STATE_KEYS
const TGT_STATE = 'CognitoSignInState';
const SIGN_IN_STATE_KEYS = {
    username: `${TGT_STATE}.username`,
    challengeName: `${TGT_STATE}.challengeName`,
    signInSession: `${TGT_STATE}.signInSession`,
    expiry: `${TGT_STATE}.expiry`,
};

const signInProps = new WeakMap();
/**
 * Helper function to wrap the sign in process for AWS Amplify in the case
 * of needing to re-authenticate for MFA challenges.
 *
 * Please see the Github issue: https://github.com/aws-amplify/amplify-js/issues/6676
 *
 * @export
 */
export function signInHelper(params, symbolToken) {
    const mergedParams = merge({}, signInProps.get(symbolToken) || {}, params);
    if (symbolToken) {
        signInProps.set(symbolToken, mergedParams);
    }
    // Call the Amplify Auth signIn method with the merged parameters
    return Auth.signIn(mergedParams);
}

/**
 * Selects and conditionally resets the MFA Challenge
 *
 * Amplify saves the MFA choice internally, and we need to have a way to change it after it's already been selected
 *
 * @param {import('@aws-amplify/auth/dist/esm/types').AuthMFAType} mfaType - The MFA type to set.
 */
export async function selectMfa(mfaType, symbolToken) {
    const currentChallenge = syncSessionStorage.getItem(SIGN_IN_STATE_KEYS.challengeName);
    let changeMfa = false;
    switch (currentChallenge) {
        case 'SELECT_MFA_TYPE':
            break;
        case 'SMS_MFA': {
            if (mfaType === 'TOTP') {
                changeMfa = true;
                break;
            }
        }
        // eslint-disable-next-line no-fallthrough
        case 'SOFTWARE_TOKEN_MFA':
            if (mfaType === 'SMS') {
                changeMfa = true;
                break;
            }
            break;
        default:
            // We could not retrieve the currentChallenge, so we assume that the UI is working correctly, but the browser cannot access the session
            changeMfa = true;
            break;
    }

    if (changeMfa) {
        /**
         *  The Amplify SDK currently does not support changing the MFA type once it's been set.
         *  As a workaround, you can sign the user out, then sign them back in, and select the desired MFA type.
         */
        try {
            await Auth.signOut();
        } catch (error) {
            // Might not actually be logged in anymore...waited too long
        }
        const signInOutput = await signInHelper({}, symbolToken);
        const { nextStep } = signInOutput;
        switch (nextStep.signInStep) {
            case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION': {
                // This is where we should get to. We now need to confirm the MFA type.
                return Auth.confirmSignIn({ challengeResponse: mfaType });
            }
            default: {
                /** Two possible scenarios:
                 * 1. User has not setup the selected MFA type
                 * 2. User has marked a different MFA type as 'PREFERRED', and Cognito is forcing it down our throats
                 *
                 * In either case, we need to dispatch the signInOutput to Redux and let our UI handle the case.
                 */
                getStoreRef().dispatch(AmplifyActions.nextStep({ nextStep }));
                return;
            }
        }
    }
}
